import React from 'react';
import { AlertTriangle, Check, Leaf, Sparkles, X } from 'lucide-react';
import { MAX_AVOIDED_INGREDIENTS, MAX_AVOIDED_INGREDIENT_LENGTH } from '../hooks/useFoodProfile';

const PRESETS = [
  ['vegan', 'Vegan', 'Keep animal-derived ingredients off the menu.'],
  ['vegetarian', 'Vegetarian', 'Flag meat, fish, gelatin, and similar ingredients.'],
  ['dairy_free', 'Dairy-free', 'Watch for milk and dairy-related ingredients.'],
  ['gluten_aware', 'Gluten-aware', 'Point out wheat and gluten-containing grains.'],
  ['allergy_watch', 'Allergy watch', 'Give detected major allergens extra attention.'],
];

const ALLERGENS = [
  ['milk', 'Milk'],
  ['egg', 'Egg'],
  ['peanut', 'Peanut'],
  ['tree_nut', 'Tree nuts'],
  ['soy', 'Soy'],
  ['wheat', 'Wheat'],
  ['sesame', 'Sesame'],
  ['fish', 'Fish'],
  ['shellfish', 'Shellfish'],
];

function ChoiceButton({ selected, label, detail, onClick }) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={`flex min-h-24 items-start gap-3 rounded-2xl border p-4 text-left transition-all ${
        selected
          ? 'border-emerald-300 bg-emerald-50 text-emerald-950 shadow-sm'
          : 'border-slate-200 bg-white text-slate-800 hover:border-emerald-200 hover:bg-emerald-50/40'
      }`}
    >
      <span className={`mt-0.5 inline-flex h-6 w-6 flex-none items-center justify-center rounded-full border ${
        selected ? 'border-emerald-600 bg-emerald-600 text-white' : 'border-slate-300 text-transparent'
      }`}>
        <Check className="h-4 w-4" />
      </span>
      <span>
        <span className="block font-bold">{label}</span>
        <span className="mt-1 block text-sm leading-5 text-slate-600">{detail}</span>
      </span>
    </button>
  );
}

function SaveStatus({ state, signedIn, error }) {
  if (state === 'loading') return <span>Loading your Food Profile...</span>;
  if (state === 'saving') return <span>Saving your choices...</span>;
  if (state === 'error') return <span className="text-red-700">{error || 'Your changes could not be saved.'}</span>;
  return (
    <span className="inline-flex items-center gap-1.5">
      <Check className="h-4 w-4" />
      {signedIn ? 'Saved to your account' : 'Saved on this device'}
    </span>
  );
}

export function FoodProfilePanel({ foodProfile, signedIn }) {
  const {
    profile,
    loading,
    saveState,
    error,
    togglePreset,
    toggleAllergen,
    addAvoidedIngredient,
    removeAvoidedIngredient,
  } = foodProfile;
  const [avoidInput, setAvoidInput] = React.useState('');
  const avoidedIngredients = profile.avoidedIngredients || [];
  const avoidListFull = avoidedIngredients.length >= MAX_AVOIDED_INGREDIENTS;

  const handleAvoidSubmit = (event) => {
    event.preventDefault();
    if (avoidListFull) return;
    addAvoidedIngredient(avoidInput);
    setAvoidInput('');
  };

  return (
    <main className="mx-auto w-full max-w-5xl px-4 py-6 sm:px-6 sm:py-10">
      <div className="mb-7">
        <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1 text-xs font-black uppercase tracking-wider text-emerald-800">
          <Sparkles className="h-3.5 w-3.5" />
          Made for you
        </div>
        <h1 className="text-3xl font-black tracking-tight text-slate-950 sm:text-4xl">Food Profile</h1>
        <p className="mt-2 max-w-2xl text-base leading-7 text-slate-600">
          Tell VegAI what matters to you. We will add helpful notes to future results while keeping the main vegan check exactly the same.
        </p>
      </div>

      <div className="space-y-6">
        <section className="rounded-[2rem] border border-white/80 bg-white/85 p-5 shadow-xl shadow-emerald-950/5 backdrop-blur sm:p-7">
          <div className="mb-5 flex items-center gap-3">
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
              <Leaf className="h-5 w-5" />
            </span>
            <div>
              <h2 className="text-xl font-black text-slate-950">My diet</h2>
              <p className="text-sm text-slate-600">Choose as many as you want. Your plate, your picks.</p>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {PRESETS.map(([id, label, detail]) => (
              <ChoiceButton
                key={id}
                selected={profile.presets.includes(id)}
                label={label}
                detail={detail}
                onClick={() => togglePreset(id)}
              />
            ))}
          </div>
        </section>

        <section className="rounded-[2rem] border border-white/80 bg-white/85 p-5 shadow-xl shadow-emerald-950/5 backdrop-blur sm:p-7">
          <div className="mb-2 flex items-start gap-3">
            <span className="inline-flex h-11 w-11 flex-none items-center justify-center rounded-2xl bg-blue-100 text-blue-700">
              <AlertTriangle className="h-5 w-5" />
            </span>
            <div>
              <h2 className="text-xl font-black text-slate-950">Allergen watch</h2>
              <p className="mt-1 text-sm leading-6 text-slate-600">
                Major allergens to flag.
              </p>
            </div>
          </div>
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {ALLERGENS.map(([id, label]) => {
              const selected = profile.watchedAllergens.includes(id);
              return (
                <button
                  key={id}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => toggleAllergen(id)}
                  className={`flex items-center gap-2 rounded-2xl border px-3 py-3 text-left text-sm font-bold transition-all ${
                    selected
                      ? 'border-blue-300 bg-blue-100 text-blue-800'
                      : 'border-slate-200 bg-white text-slate-700 hover:border-blue-200 hover:bg-blue-50'
                  }`}
                >
                  <span className={`inline-flex h-5 w-5 flex-none items-center justify-center rounded-md border ${
                    selected ? 'border-blue-700 bg-blue-700 text-white' : 'border-slate-300 text-transparent'
                  }`}>
                    <Check className="h-3.5 w-3.5" />
                  </span>
                  {label}
                </button>
              );
            })}
          </div>
        </section>

        <section className="rounded-[2rem] border border-white/80 bg-white/85 p-5 shadow-xl shadow-emerald-950/5 backdrop-blur sm:p-7">
          <div className="flex items-start gap-3">
            <span className="inline-flex h-11 w-11 flex-none items-center justify-center rounded-2xl bg-orange-100 text-orange-700">
              <X className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <h2 className="text-xl font-black text-slate-950">Avoid</h2>
              <p className="mt-1 text-sm leading-6">
                Add ingredients you personally avoid.
              </p>
              <form className="mt-4 flex flex-col gap-2 sm:flex-row" onSubmit={handleAvoidSubmit}>
                <label className="sr-only" htmlFor="avoided-ingredient-input">Add avoided ingredient</label>
                <input
                  id="avoided-ingredient-input"
                  type="text"
                  value={avoidInput}
                  maxLength={MAX_AVOIDED_INGREDIENT_LENGTH}
                  disabled={avoidListFull}
                  onChange={(event) => setAvoidInput(event.target.value)}
                  placeholder={avoidListFull ? 'Avoid list is full' : 'Palm oil'}
                  className="min-h-12 flex-1 rounded-2xl border border-slate-200 bg-white px-4 text-base font-semibold text-slate-900 shadow-sm outline-none transition focus:border-orange-300 focus:ring-4 focus:ring-orange-100 disabled:bg-slate-100 disabled:text-slate-500"
                />
                <button
                  type="submit"
                  disabled={avoidListFull || !avoidInput.trim()}
                  className="inline-flex min-h-12 items-center justify-center rounded-2xl bg-orange-500 px-5 font-bold text-white shadow-sm transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:bg-slate-300"
                >
                  Add
                </button>
              </form>
              <p className="mt-2 text-xs font-semibold text-slate-500">
                One ingredient at a time. {avoidedIngredients.length}/{MAX_AVOIDED_INGREDIENTS} saved.
              </p>
              {avoidedIngredients.length > 0 ? (
                <div className="mt-4 flex flex-wrap gap-2">
                  {avoidedIngredients.map((ingredient) => (
                    <span
                      key={ingredient.toLowerCase()}
                      className="inline-flex items-center gap-2 rounded-full border border-slate-900 bg-white px-3 py-1.5 text-sm font-bold text-slate-950"
                    >
                      {ingredient}
                      <button
                        type="button"
                        onClick={() => removeAvoidedIngredient(ingredient)}
                        className="inline-flex h-5 w-5 items-center justify-center rounded-full text-slate-800 hover:bg-slate-100"
                        aria-label={`Remove ${ingredient}`}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  ))}
                </div>
              ) : (
                <p className="mt-4 rounded-2xl border border-dashed border-orange-200 bg-orange-50/60 px-4 py-3 text-sm font-semibold text-orange-900">
                  Nothing on your no-thanks list yet.
                </p>
              )}
            </div>
          </div>
        </section>

        <section className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-950">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 flex-none text-amber-700" />
            <p>
              <strong>Food Profile is informational, not medical advice.</strong> VegAI can miss ingredients, cross-contact warnings, or recipe changes. Always check the current package label and follow guidance from your medical professional.
            </p>
          </div>
        </section>

        <div className="flex min-h-10 items-center justify-center rounded-2xl bg-white/75 px-4 py-2 text-sm font-bold text-emerald-800 shadow-sm" role="status" aria-live="polite">
          {loading ? 'Loading your Food Profile...' : <SaveStatus state={saveState} signedIn={signedIn} error={error} />}
        </div>
      </div>
    </main>
  );
}
